/**
 * pages/NotificationsPage.tsx — CLI-451
 * Уведомления пациента (LAB_ORDER_COMPLETED, EXAM_READY, ...).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getNotifications, markNotificationRead } from '../services/patient.service';

const TYPE_ICON: Record<string, string> = {
  LAB_ORDER_COMPLETED:   '🧪',
  LAB_RESULT_CRITICAL:   '🚨',
  EXAM_READY:            '📋',
  EXAM_SIGN_REQUIRED:    '✍️',
  APPOINTMENT_REMINDER:  '📅',
  APPOINTMENT_CONFIRMED: '✅',
  APPOINTMENT_CANCELLED: '❌',
  APPOINTMENT_NEW:       '🗓️',
  SYSTEM:                'ℹ️',
  DEFAULT:               '🔔',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export function NotificationsPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-notifications'],
    queryFn: getNotifications,
  });

  const readMut = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patient-notifications'] }),
  });

  if (isLoading) return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 4 }).map((_, i) => <div key={i} className="bg-white rounded-xl h-16 animate-pulse" />)}
    </div>
  );

  if (isError) return <div className="p-4 text-center text-red-500">Ошибка загрузки уведомлений.</div>;

  if (!data?.data.length) return (
    <div className="p-4 text-center text-gray-500 mt-12">
      <p className="text-4xl mb-3">🔔</p>
      <p className="font-medium text-gray-700">Уведомлений пока нет</p>
    </div>
  );

  return (
    <div className="p-4 space-y-3">
      <h2 className="text-lg font-bold text-gray-800">
        Уведомления
        {(data.meta.unread) > 0 && (
          <span className="ml-2 text-xs bg-primary-600 text-white px-2 py-0.5 rounded-full">
            {data.meta.unread}
          </span>
        )}
      </h2>
      {data.data.map((n) => (
        <div
          key={n.id}
          className={`bg-white rounded-xl p-4 shadow-sm border transition ${
            n.isRead ? 'border-gray-100 opacity-75' : 'border-primary-200 bg-primary-50/30'
          }`}
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl">{TYPE_ICON[n.type] ?? TYPE_ICON['DEFAULT']}</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm">{n.title}</p>
              <p className="text-sm text-gray-600 mt-0.5">{n.message}</p>
              <p className="text-xs text-gray-400 mt-1">{formatDate(n.createdAt)}</p>
            </div>
            {!n.isRead && (
              <button
                onClick={() => readMut.mutate(n.id)}
                className="shrink-0 text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                ✓ Прочитано
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
